import { ChangeDetectionStrategy, Component, effect, inject, NgZone } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { SplashScreen } from '@capacitor/splash-screen';
import { MenuController, ModalController, NavController, Platform } from '@ionic/angular/lazy';
import { TranslateService } from '@ngx-translate/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { addIcons } from 'ionicons';
import {
  alertCircle,
  alertOutline,
  checkmarkCircle,
  checkmarkDoneOutline,
  checkmarkOutline,
  chevronDownOutline,
  chevronUpOutline,
  closeCircle,
  closeOutline,
  codeDownloadOutline,
  contractOutline,
  copyOutline,
  ellipsisVertical,
  expandOutline,
  filterOutline,
  flashlightOutline,
  gitCommitOutline,
  helpOutline,
  homeOutline,
  hourglassOutline,
  lockClosedOutline,
  logOutOutline,
  moveOutline,
  pencilOutline,
  refreshOutline,
  removeCircleOutline,
  repeatOutline,
  saveOutline,
  searchOutline,
  settingsOutline,
  shieldCheckmarkOutline,
  speedometerOutline,
  syncOutline,
  warningOutline,
  wifiOutline,
} from 'ionicons/icons';

import { FONT_SIZES } from '@/app/app/constants/font-size.const';
import { TenantDeploymentComponent } from '@/app/app/modals/tenant-deployment/tenant-deployment.component';
import { SupportedVersionService } from '@/app/app/services/app/supported-version.service';
import { PushNotificationService } from '@/app/app/services/communications/push-notification.service';
import { odsIcons } from '@/app/assets/icon/ods-icon';
import { environment } from '@/app/environments/environment';
import { isNotNullOrEmpty } from '@/shared/lib/core/utils/data-validation.util';
import { CoreModule } from '@/shared/lib/modules/core.module';
import { MessageService } from '@/shared/lib/services/communications/message.service';
import { DataStore } from '@/shared/lib/stores/data.store';
import { DeploymentStore } from '@/shared/lib/stores/deployment.store';
import { NotificationStore } from '@/shared/lib/stores/notification.store';
import { SettingsStore } from '@/shared/lib/stores/settings.store';
import { UserStore } from '@/shared/lib/stores/user.store';

SafeArea.getSafeAreaInsets().then((data) => {
  const { insets } = data;
  document.body.style.setProperty('--ion-safe-area-top', `${insets.top}px`);
  document.body.style.setProperty(
    '--ion-safe-area-right',
    `${insets.right}px`,
  );
  document.body.style.setProperty(
    '--ion-safe-area-bottom',
    `${insets.bottom}px`,
  );
  document.body.style.setProperty(
    '--ion-safe-area-left',
    `${insets.left}px`,
  );
});

// Time after resuming before checking that a page is visible (ms)
const RESUME_CHECK_DELAY = 1500;

@Component({
  selector: 'ods-root',
  imports: [CoreModule, RouterModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  userStore = inject(UserStore);
  deploymentStore = inject(DeploymentStore);
  notificationStore = inject(NotificationStore);
  settingsStore = inject(SettingsStore);
  dataStore = inject(DataStore);

  zone = inject(NgZone);
  platform = inject(Platform);
  message = inject(MessageService);
  menu = inject(MenuController);
  router = inject(Router);
  navController = inject(NavController);
  translate = inject(TranslateService);
  modalController = inject(ModalController);
  supportedVersionService = inject(SupportedVersionService);
  pushNotificationService = inject(PushNotificationService);

  public readonly isDevelopment = !environment.production;
  userObs = toObservable(this.userStore.isAuthenticated);

  constructor() {
    addIcons({
      'log-out-outline': logOutOutline,
      'code-download-outline': codeDownloadOutline,
      'refresh-outline': refreshOutline,
      'chevron-down-outline': chevronDownOutline,
      'checkmark-done-circle-outline': checkmarkDoneOutline,
      'sync-outline': syncOutline,
      'wifi-outline': wifiOutline,
      'chevron-up-outline': chevronUpOutline,
      'remove-circle-outline': removeCircleOutline,
      'checkmark-done-outline': checkmarkDoneOutline,
      'pencil-outline': pencilOutline,
      'lock-closed-outline': lockClosedOutline,
      'home-outline': homeOutline,
      'flashlight-outline': flashlightOutline,
      'shield-checkmark-outline': shieldCheckmarkOutline,
      'save-outline': saveOutline,
      'settings-outline': settingsOutline,
      'move-outline': moveOutline,
      'search-outline': searchOutline,
      'repeat-outline': repeatOutline,
      'filter-outline': filterOutline,
      'alert-outline': alertOutline,
      'checkmark-outline': checkmarkOutline,
      'help-outline': helpOutline,
      'close-outline': closeOutline,
      'speedometer-outline': speedometerOutline,
      'git-commit-outline': gitCommitOutline,
      'hourglass-outline': hourglassOutline,
      'copy-outline': copyOutline,
      'alert-circle': alertCircle,
      'ellipsis-vertical': ellipsisVertical,
      'close-circle': closeCircle,
      'contract-outline': contractOutline,
      'expand-outline': expandOutline,
      'warning-outline': warningOutline,
      'checkmark-circle': checkmarkCircle,
    });
    this.translate.setDefaultLang('en');

    // Root font size from the user settings; everything sized in rem (including most Ionic components) scales with it
    effect(() => {
      document.documentElement.style.fontSize = FONT_SIZES[this.settingsStore.fontSize()];
    });

    this.platform.ready().then(() => {
      this.initializeApp();
    });
    this.platform.resume.subscribe(() => {
      this.zone.run(() => {
        this.reInitializeApp();
      });
    });
  }

  initializeApp = async () => {
    await SplashScreen.show().catch(() => undefined);
    await SplashScreen.hide().catch(() => undefined);
    this.loadDeviceInfo();
    // Subscribed once: loads the user data now when logged in, and again after every login
    this.userObs.subscribe((authStatus: boolean) => {
      if (authStatus) {
        this.loadUserData();
      }
    });
  };

  reInitializeApp = async () => {
    try {
      await SplashScreen.show().catch(() => undefined);
      this.loadDeviceInfo();
      if (this.userStore.isAuthenticated()) {
        this.loadUserData();
      } else {
        // Root navigation, like the tabs shell does on logout: the login page replaces the shell instead of being pushed on it
        await this.navController.navigateRoot(['/account/login']);
      }
    } finally {
      // Always hidden, otherwise the app stays on the splash screen (e.g. resumed after the login expired)
      await SplashScreen.hide().catch(() => undefined);
    }
    setTimeout(() => this.recoverBlankScreen(), RESUME_CHECK_DELAY);
  };

  loadUserData() {
    this.notificationStore.setSignalRConnectionState(1);
    this.deploymentStore.loadTenantList(false);
    this.settingsStore.load();
    this.translate.setDefaultLang(this.userStore.displayLanguage());
    this.supportedVersionService.getSupportedVersion();
    this.pushNotificationService.initialize();
  }

  // After resuming, a page can be left blank (e.g. a page transition interrupted by going to the background leaves it
  // hidden). Then the app goes to the home page, and when that doesn't render either, the app is reloaded on the home page.
  private async recoverBlankScreen(retry = true) {
    if (this.hasVisiblePage()) {
      return;
    }
    if (!retry) {
      this.message.warn('AppComponent', 'Resumed without a visible page, reloading the app');
      window.location.replace('/home');
      return;
    }
    this.message.warn('AppComponent', 'Resumed without a visible page, going to home');
    await this.modalController.getTop().then(modal => modal?.dismiss()).catch(() => undefined);
    // Root navigation also clears the stuck page's navigation stack
    await this.navController.navigateRoot(['/home']).catch(() => false);
    setTimeout(() => this.recoverBlankScreen(false), RESUME_CHECK_DELAY);
  }

  // A page is visible when an Ionic page without a router outlet inside it (so not the tabs shell) is shown, and none of
  // its parent pages is hidden or still invisible from a transition.
  private hasVisiblePage(): boolean {
    return Array.from(document.querySelectorAll('.ion-page'))
      .some(page => !page.querySelector('ion-router-outlet') && !page.closest('.ion-page-hidden, .ion-page-invisible'));
  }

  async loadDeviceInfo() {
    if (Device) {
      Device.getInfo().then(x => {
        this.dataStore.setDeviceInfo(x);
        if (x.platform !== 'web') {
          App.getInfo().then(appInfo => this.dataStore.setAppInfo(appInfo));
        }
      });
      Device.getId().then(x => this.dataStore.setDeviceId(x.identifier));
    }
  }

  closeMenu() {
    this.menu.close();
  }

  public logOut() {
    this.userStore.clear();
    this.deploymentStore.clear();
    this.settingsStore.clear();
    this.notificationStore.clear();
    window.location.reload();
  }

  async presentTenantDeploymentModal() {
    const modal = await this.modalController.create({
      component: TenantDeploymentComponent,
      cssClass: 'settingsModal',
      canDismiss: true,
      componentProps: {},
    });
    return await modal.present();
  }

  protected readonly isNotNullOrEmpty = isNotNullOrEmpty;
  protected readonly odsIcons = odsIcons;
}
